import posthog from "posthog-js";
import { shouldDisableAdTracking } from "./privacySignals";

const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";
const POSTHOG_DEFAULTS_VERSION = "2026-01-30";

type Primitive = string | number | boolean | null;
export type PostHogEventProperties = Record<string, Primitive | undefined>;

interface PostHogUserProperties {
  email?: string | null;
}

export function getPostHogApiKey(): string | null {
  const key =
    import.meta.env.VITE_PUBLIC_POSTHOG_KEY?.trim() ||
    import.meta.env.VITE_POSTHOG_KEY?.trim();
  return key ? key : null;
}

export function getPostHogHost(): string {
  const configuredHost =
    import.meta.env.VITE_PUBLIC_POSTHOG_HOST?.trim() ||
    import.meta.env.VITE_POSTHOG_HOST?.trim();
  return configuredHost || DEFAULT_POSTHOG_HOST;
}

function toPostHogProperties(
  properties?: PostHogEventProperties,
): Record<string, Primitive> | undefined {
  if (!properties) return undefined;

  const sanitizedEntries = Object.entries(properties).filter(
    (entry): entry is [string, Primitive] => entry[1] !== undefined,
  );

  if (sanitizedEntries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(sanitizedEntries);
}

export function isPostHogEnabled(): boolean {
  return Boolean(getPostHogApiKey());
}

export function initPostHog(): void {
  if (typeof window === "undefined") {
    return;
  }

  const key = getPostHogApiKey();
  if (!key) {
    return;
  }

  posthog.init(key, getPostHogOptions());
}

export function getPostHogOptions() {
  return {
    api_host: getPostHogHost(),
    defaults: POSTHOG_DEFAULTS_VERSION,
    capture_pageview: "history_change",
    capture_pageleave: "if_capture_pageview",
    mask_all_text: true,
    mask_all_element_attributes: true,
    disable_session_recording: true,
    loaded: () => {
      syncPostHogConsent();
    },
  } as const;
}

function canUsePostHog(): boolean {
  return typeof window !== "undefined" && isPostHogEnabled();
}

export function identifyPostHogUser(
  userId: string,
  properties?: PostHogUserProperties,
): void {
  if (!canUsePostHog() || !userId) {
    return;
  }

  const userProperties = toPostHogProperties({
    email: properties?.email ?? undefined,
  });
  posthog.identify(userId, userProperties);
}

export function capturePostHogEvent(
  event: string,
  properties?: PostHogEventProperties,
): void {
  if (!canUsePostHog() || !event) {
    return;
  }

  posthog.capture(event, toPostHogProperties(properties));
}

export function resetPostHog(): void {
  if (!canUsePostHog()) {
    return;
  }

  posthog.reset();
  syncPostHogConsent();
}

export function syncPostHogConsent(userDbPreference?: boolean): void {
  if (!canUsePostHog()) {
    return;
  }

  if (shouldDisableAdTracking(userDbPreference)) {
    posthog.opt_out_capturing();
    return;
  }

  posthog.opt_in_capturing();
}
